import { createContext, ReactElement, ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomMusic,
  CustomMusicManifest,
  HymnalManifest,
  Org,
  OrganizationKey,
  OrgId,
  SongFileExtension,
  SongManifestEntry,
  SongSlug,
} from '../../../common/src/model.ts';
import { getOrgId } from '../../../common/src/model_util.ts';
import {
  customMusicDir,
  getOrgCustomMusicManifestPathFromKey,
  musicManifestFilename,
  parseSongSlug,
} from '../../../common/src/paths.ts';
import { getCachedResponse, putCachedResponse } from './client_cache_storage.ts';
import { localStorageGet, LocalStorageKey, localStorageSet } from './client_local_storage.ts';
import { reconcileCustomMusic } from './hymn_identity.ts';
import { useCurrentOrg } from './use_current_org.tsx';
import { ContentKind, useFetch, useFetchArray } from './use_fetch.tsx';
import { getHymnsFromHymnalManifest } from './use_hymnals.tsx';

const customMusicManifestPath = `/${customMusicDir}/${musicManifestFilename}`;

interface CustomMusicContextInterface {
  customMusic: CustomMusic;
  customMusicManifest: CustomMusicManifest | undefined;
  // The orgs whose custom music is known: their song manifest has arrived, or the root manifest
  // says they publish none. `customMusic` cannot tell the two apart from "still loading" - an org
  // gets an empty list the moment it is requested - and the org hymnal pre-download must not run
  // until the church's own songs are known, or it skips them all and records the church as done.
  customMusicLoadedOrgIds: ReadonlySet<OrgId>;
  registerAdditionalOrgIds: (orgId: OrgId[]) => void;
  unregisterAdditionalOrgIds: (orgId: OrgId[]) => void; // must be the original array instance
  setIncludeAllOrgs: () => void;
  clearIncludeAllOrgs: () => void;
}

export function useCustomMusic(): CustomMusicContextInterface {
  return use(CustomMusicContext);
}

const CustomMusicContext = createContext<CustomMusicContextInterface>({} as any);

export const CustomMusicProvider = ({ children }: { children: ReactNode }) => {
  const { currentOrg } = useCurrentOrg();
  const [additionalOrgIdLists, setAdditionalOrgIdLists] = useState<OrgId[][]>(() => []);
  const [includeAllOrgsCount, setIncludeAllOrgsCount] = useState(0);
  const additionalOrgIds = useMemo(() => additionalOrgIdLists.flatMap((orgIds) => orgIds), [additionalOrgIdLists]);
  const { customMusic, customMusicManifest, customMusicLoadedOrgIds, fetchWorkers } = useCustomMusicDownload({
    orgId: currentOrg?.id,
    additionalOrgIds,
    includeAllOrgs: includeAllOrgsCount !== 0,
  });

  const registerAdditionalOrgIds = useCallback((orgIds: OrgId[]) => {
    setAdditionalOrgIdLists((additionalOrgIds) => [...additionalOrgIds, orgIds]);
  }, []);

  const unregisterAdditionalOrgIds = useCallback((orgIds: OrgId[]) => {
    setAdditionalOrgIdLists((additionalOrgIds) => [
      ...new Set(additionalOrgIds.filter((currentOrgIds) => currentOrgIds !== orgIds)),
    ]);
  }, []);

  const setIncludeAllOrgs = useCallback(() => {
    setIncludeAllOrgsCount((includeAllOrgsCount) => includeAllOrgsCount + 1);
  }, []);

  const clearIncludeAllOrgs = useCallback(() => {
    setIncludeAllOrgsCount((includeAllOrgsCount) => includeAllOrgsCount - 1);
  }, []);

  return (
    <CustomMusicContext
      value={{
        customMusic,
        customMusicManifest,
        customMusicLoadedOrgIds,
        registerAdditionalOrgIds,
        unregisterAdditionalOrgIds,
        setIncludeAllOrgs,
        clearIncludeAllOrgs,
      }}
    >
      {children}
      {fetchWorkers}
    </CustomMusicContext>
  );
};

function useCustomMusicDownload({
  orgId,
  additionalOrgIds,
  includeAllOrgs,
}: { orgId?: OrgId; additionalOrgIds?: OrgId[]; includeAllOrgs?: boolean } = {}): {
  customMusic: CustomMusic;
  customMusicManifest: CustomMusicManifest | undefined;
  customMusicLoadedOrgIds: ReadonlySet<OrgId>;
  fetchWorkers: ReactElement;
} {
  const [customMusic, setCustomMusic] = useState<CustomMusic>({});
  const { content: customMusicManifest } = useFetch<CustomMusicManifest>({
    url: customMusicManifestPath,
    contentKind: ContentKind.JSON,
  });
  const orgKeys = useMemo(
    () =>
      customMusicManifest
        ? Object.keys(customMusicManifest).filter(
            (key) => includeAllOrgs || orgId === getOrgId(key) || additionalOrgIds?.includes(getOrgId(key)),
          )
        : [],
    [additionalOrgIds, customMusicManifest, includeAllOrgs, orgId],
  );
  const orgManifestUrls = useMemo(
    () => orgKeys.map((key) => getOrgCustomMusicManifestPathFromKey(key, { encodeURI: true })),
    [orgKeys],
  );
  const { results: orgManifests, fetchWorkers } = useFetchArray<HymnalManifest>(orgManifestUrls, ContentKind.JSON);
  const orgManifestsByKey: Record<OrganizationKey, HymnalManifest | undefined> = useMemo(
    () => Object.fromEntries([...orgManifests.entries()].map(([index, res]) => [orgKeys[index], res.content])),
    [orgKeys, orgManifests],
  );

  const customMusicLoadedOrgIds = useMemo(() => {
    const loaded = new Set<OrgId>();
    if (!customMusicManifest) {
      return loaded;
    }
    // requested orgs the root manifest does not list publish no custom music: known, and empty
    for (const requestedOrgId of [orgId, ...(additionalOrgIds ?? [])]) {
      if (requestedOrgId !== undefined && !orgKeys.some((key) => getOrgId(key) === requestedOrgId)) {
        loaded.add(requestedOrgId);
      }
    }
    for (const [index, key] of orgKeys.entries()) {
      if (orgManifests[index]?.content) {
        loaded.add(getOrgId(key));
      }
    }
    return loaded;
  }, [additionalOrgIds, customMusicManifest, orgId, orgKeys, orgManifests]);

  // the last map we produced, so a custom music manifest refresh leaves every unchanged song (and
  // the map itself, when nothing changed) referentially identical (issue #3517 item A3)
  const customMusicRef = useRef<CustomMusic>(customMusic);

  useEffect(() => {
    const hymnLists = Object.entries(orgManifestsByKey).map(([key, hymnalManifest]) => [
      getOrgId(key),
      hymnalManifest
        ? getHymnsFromHymnalManifest({
            hymnalName: key,
            orgId: getOrgId(key),
            hymnalManifest,
            basePath: `/${customMusicDir}`,
          })
        : [],
    ]);
    // make sure the array has at least one key (current org) with an empty list to indicate download complete
    const reconciled = reconcileCustomMusic(customMusicRef.current, {
      ...(orgId && { [orgId]: [] }),
      ...Object.fromEntries(hymnLists),
    });
    customMusicRef.current = reconciled;
    setCustomMusic(reconciled);
  }, [orgId, customMusicManifest, additionalOrgIds, orgManifestsByKey]);

  return { customMusic, customMusicManifest, customMusicLoadedOrgIds, fetchWorkers };
}

function getOrgCustomMusicManifestPath(customMusicManifest: CustomMusicManifest, orgId: OrgId): string | undefined {
  const key = Object.keys(customMusicManifest).find((key) => getOrgId(key) === orgId);
  return key ? getOrgCustomMusicManifestPathFromKey(key, { encodeURI: true }) : undefined;
}

export async function recordRecentlyAddedSong({
  customMusicManifest,
  org,
  songSlug,
  songName,
  psalmNumber,
}: {
  customMusicManifest: CustomMusicManifest | undefined;
  org: Org;
  songSlug: SongSlug;
  songName: string;
  psalmNumber?: number;
}) {
  if (!customMusicManifest) {
    console.error(`Unable to record recently deleted song without custom music manifest`);
    return;
  }
  const manifestUrl = getOrgCustomMusicManifestPath(customMusicManifest, org.id);
  if (!manifestUrl) {
    console.error(`Unable to find manifest for org ${org.id}`);
    return;
  }
  const response = await getCachedResponse(manifestUrl);
  if (!response) {
    console.error(`Unable to find cached manifest for ${manifestUrl}`);
    return;
  }
  const manifest = (await response.json()) as HymnalManifest;
  const songNumber = parseSongSlug(songSlug).songNumber;
  let updatedHymn: SongManifestEntry;
  if (manifest[songNumber]) {
    const { psalm: _, ...hymnWithNoPsalm } = manifest[songNumber];
    updatedHymn = { ...hymnWithNoPsalm, title: songName, ...(psalmNumber && { psalm: `Psalm ${psalmNumber}` }) };
  } else {
    updatedHymn = {
      title: songName,
      ...(psalmNumber && { psalm: `Psalm ${psalmNumber}` }),
      hash: '00000000',
      extensions: [SongFileExtension.Pdf],
    };
    const orgHymnal = localStorageGet(LocalStorageKey.OrgHymnal);
    localStorageSet(LocalStorageKey.OrgHymnal, {
      ...orgHymnal,
      songs: [...orgHymnal.songs, { slug: songSlug, count: 0, dates: [] }],
    });
  }
  const updatedManifest = { ...manifest, [songNumber]: updatedHymn };
  await putCachedResponse(manifestUrl, new Response(JSON.stringify(updatedManifest)));
}

export async function recordRecentlyDeletedSong(
  customMusicManifest: CustomMusicManifest | undefined,
  orgId: OrgId,
  slug: SongSlug,
) {
  if (!customMusicManifest) {
    console.error(`Unable to record recently deleted song without custom music manifest`);
    return;
  }
  const manifestUrl = getOrgCustomMusicManifestPath(customMusicManifest, orgId);
  if (!manifestUrl) {
    console.error(`Unable to find manifest for org ${orgId}`);
    return;
  }
  const response = await getCachedResponse(manifestUrl);
  if (!response) {
    console.error(`Unable to find cached manifest for ${manifestUrl}`);
    return;
  }
  const manifest = (await response.json()) as HymnalManifest;
  const songNumber = parseSongSlug(slug).songNumber;
  if (!manifest[songNumber]) {
    console.warn(`Song ${slug} was not in org custom music manifest ${manifestUrl}`);
    return;
  }
  const { [songNumber]: _, ...updatedManifest } = manifest;
  await putCachedResponse(manifestUrl, new Response(JSON.stringify(updatedManifest)));
}
